import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { canSee, visibleNav, ROLE_LABEL } from "../lib/nav";
import api from "../lib/api";
import { Avatar } from "./ui/kit";
import { ZvonekPanel, useUpozorneni } from "./Zvonek";
import { UpozorneniPanel } from "./Upozorneni";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo-profitpilot.png";

function Item({ item, collapsed, sub = false, count }) {
  const Icon = Icons[item.icon] || Icons.Circle;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      data-testid={`nav-${item.key}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-icon h-9 text-[13px] transition-colors duration-150 ${
          sub ? "pl-7 pr-3 font-500" : "px-3 font-600"
        } ${
          isActive
            ? "bg-sidebar-3 text-ondark"
            : "text-ondark-muted hover:text-ondark hover:bg-sidebar-1"
        } ${collapsed ? "justify-center px-0 pl-0" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={sub ? 15 : 17} strokeWidth={1.7}
            className={isActive ? "text-accent" : "text-current"} />
          {collapsed && item.badge && (
            <span className="absolute top-1.5 right-[14px] h-2 w-2 rounded-full bg-danger ring-2 ring-sidebar" />
          )}
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.badge && (
            <span className="ml-auto amount text-[10px] bg-danger text-white rounded-pill px-1.5 py-0.5 leading-none">
              {item.badge}
            </span>
          )}
          {!collapsed && !item.badge && count != null && (
            <span className="ml-auto amount text-[10.5px] text-ondark-muted">{count}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

/** Skupina s podmoduly — rozbalená, dokud ji uživatel nesbalí. */
function Group({ item, collapsed, counts }) {
  const Icon = Icons[item.icon] || Icons.Circle;
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(true);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

  if (collapsed) {
    return <>{item.children.map((c) => <Item key={c.key} item={c} collapsed />)}</>;
  }
  return (
    <div>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={`nav-group-${item.key}`}
        className={`w-full flex items-center gap-3 rounded-icon px-3 h-9 text-[13px] font-600 transition-colors ${
          childActive ? "text-ondark" : "text-ondark-muted hover:text-ondark hover:bg-sidebar-1"}`}
      >
        <Icon size={17} strokeWidth={1.7} className={childActive ? "text-accent" : "text-current"} />
        <span className="truncate">{item.label}</span>
        <Icons.ChevronUp size={14} strokeWidth={2}
          className={`ml-auto transition-transform ${open ? "" : "rotate-180"}`} />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5 border-l border-sidebar-3 ml-[19px]">
          {item.children.map((c) => (
            <Item key={c.key} item={c} sub count={c.countKey ? counts[c.countKey] : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed = false, onNavigate, onHledat, zvonek = false }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const upozorneni = useUpozorneni();
  const [zvonekOtevren, setZvonekOtevren] = useState(false);
  useEffect(() => { setZvonekOtevren(false); }, [pathname]);
  const prepniZvonek = () => {
    if (!zvonekOtevren) upozorneni.obnovit();
    setZvonekOtevren(!zvonekOtevren);
  };
  const items = visibleNav(user, canSee);
  const [counts, setCounts] = useState({});
  const [servisBadge, setServisBadge] = useState(0);
  const [clenstvi, setClenstvi] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/api/dashboard/summary")
      .then(({ data }) => { if (alive) setCounts(data || {}); })
      .catch(() => {});
    api.get("/api/servis/badge")
      .then(({ data }) => { if (alive) setServisBadge(data?.count || 0); })
      .catch(() => {});
    api.get("/api/auth/clenstvi")
      .then(({ data }) => { if (alive) setClenstvi(data?.items || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [menu, setMenu] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);

  const switchAccount = async (m) => {
    if (m.current || switching) return;
    setSwitching(true);
    try {
      await api.post("/api/auth/prepnout", { membership_id: m.membership_id });
      window.location.assign("/");
    } catch { setSwitching(false); }
  };

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (path) => {
    setMenu(false);
    if (onNavigate) onNavigate();
    navigate(path);
  };

  const doLogout = async () => {
    setMenu(false);
    await logout();
    navigate("/login");
  };

  return (
    <aside
      className={`h-full bg-sidebar flex flex-col transition-[width] duration-200 motion-reduce:transition-none ${collapsed ? "w-[68px]" : "w-[236px]"}`}
      onClick={onNavigate}
    >
      {/* brand */}
      <div className={`flex items-center gap-2.5 px-4 h-16 ${collapsed ? "justify-center px-0" : ""}`}>
        <img src={logo} alt="ProfitPilot" className="h-8 w-8 shrink-0" data-testid="sidebar-logo" />
        {!collapsed && (
          <span className="text-ondark font-800 text-[15px] tracking-tight">
            ProfitPilot<span className="text-ondark-muted align-super text-[9px]">™</span>
          </span>
        )}
        {zvonek && !collapsed && (
          <span className="ml-auto -mr-1"><ZvonekPanel onClick={prepniZvonek} otevreno={zvonekOtevren} /></span>
        )}
      </div>
      {zvonek && collapsed && (
        <div className="flex justify-center pb-2"><ZvonekPanel collapsed onClick={prepniZvonek} otevreno={zvonekOtevren} /></div>
      )}
      {zvonek && zvonekOtevren && <UpozorneniPanel onClose={() => setZvonekOtevren(false)} />}

      {/* search */}
      {!collapsed && (
        <div className="px-3 pb-2">
          <button
            data-testid="sidebar-search"
            onClick={(e) => { e.stopPropagation(); onHledat && onHledat(); }}
            className="w-full flex items-center gap-2 h-9 rounded-icon bg-sidebar-1 text-ondark-muted px-3 text-[12.5px] hover:bg-sidebar-2 transition-colors"
          >
            <Icons.Search size={15} strokeWidth={1.7} />
            <span>Hledat…</span>
            <span className="ml-auto amount text-[10px] bg-sidebar-3 rounded px-1.5 py-0.5">⌘K</span>
          </button>
        </div>
      )}

      {/* nav */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto pt-1">
        {items.map((i) => (
          i.children
            ? <Group key={i.key} item={i} collapsed={collapsed} counts={counts} />
            : <Item key={i.key} item={i.key === "servis" && servisBadge ? { ...i, badge: servisBadge } : i} collapsed={collapsed} />
        ))}
      </nav>

      {/* footer user */}
      <div ref={ref} onClick={(e) => e.stopPropagation()} className={`relative border-t border-sidebar-3 p-3 ${collapsed ? "flex justify-center" : ""}`}>
        {menu && (
          <div
            className={`absolute bottom-[68px] left-3 card shadow-panel p-1.5 rise z-40 ${collapsed ? "w-[220px]" : "right-3"}`}
            data-testid="user-menu"
          >
            {clenstvi.length > 1 && (
              <div className="pb-1.5 mb-1.5 border-b border-line-softer" data-testid="account-switcher">
                <div className="label-up text-muted px-3 pt-1 pb-1.5">Účty</div>
                {clenstvi.map((m) => (
                  <button
                    key={m.membership_id}
                    onClick={() => switchAccount(m)}
                    disabled={m.current || switching}
                    data-testid={`account-${m.company_id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-icon text-left ${m.current ? "bg-inputbg" : "hover:bg-inputbg"}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-700 text-ink truncate">{m.company_name}</span>
                      <span className="block text-[11px] text-muted truncate">
                        {(m.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ")}
                      </span>
                    </span>
                    {m.current
                      ? <span className="h-2 w-2 rounded-full bg-accent shrink-0" data-testid="account-current-dot" />
                      : <Icons.ArrowRightLeft size={14} className="text-muted shrink-0" />}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => go("/nastaveni")}
              data-testid="nav-nastaveni"
              className="w-full flex items-center gap-2.5 px-3 h-9 rounded-icon text-[13px] font-600 text-sec hover:bg-inputbg"
            >
              <Icons.Settings size={16} strokeWidth={1.7} /> Nastavení
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="w-full flex items-center gap-2.5 px-3 h-9 rounded-icon text-[13px] font-600 text-danger hover:bg-[#fdecea]"
            >
              <Icons.LogOut size={16} strokeWidth={1.7} /> Odhlásit se
            </button>
          </div>
        )}
        <button
          onClick={() => setMenu((v) => !v)}
          data-testid="user-menu-button"
          className={`flex items-center gap-2.5 w-full rounded-icon p-1 hover:bg-sidebar-1 transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <Avatar name={user?.name} url={user?.photo_url} size={collapsed ? 32 : 34} />
          {!collapsed && (
            <div className="min-w-0 text-left flex-1">
              <div className="text-ondark text-[12.5px] font-600 truncate">{user?.name}</div>
              <div className="text-ondark-muted text-[11px] truncate">{company?.name}</div>
            </div>
          )}
          {!collapsed && clenstvi.length > 1 && (
            <Icons.ChevronsUpDown size={15} strokeWidth={1.7} className="text-ondark-muted shrink-0" />
          )}
        </button>
      </div>
    </aside>
  );
}
